// Pure tab-state logic for the workspace shell (PRD-0085, slice 1).
//
// Kept as pure functions (no React) so the open/close/dedupe behavior is
// unit-testable WITHOUT rendering — avoids the project's known Bun
// `mock.module` pollution and keeps the reducer the single source of truth.

export type WorkTabKind =
  | "home"
  | "brief"
  | "review"
  | "output"
  | "draft"
  | "artifact"
  | "docs"
  | "assets"
  | "tasks"
  | "workflows"
  | "chat"
  | "analytics";

export interface WorkTab {
  /** Stable id. For Home this is always `HOME_TAB.id`. */
  id: string;
  kind: WorkTabKind;
  label: string;
  /**
   * Deep-link route the tab currently points at. This MOVES as the operator drills
   * within one work object (e.g. analytics property -> source -> keyword), so it is the
   * tab's restore point, NOT its identity. Identity is `id`.
   */
  route: string;
}

export interface WorkspaceTabsState {
  tabs: WorkTab[];
  activeId: string;
}

/** The pinned, non-closeable Home tab. Always `tabs[0]`. */
export const HOME_TAB: WorkTab = {
  id: "home",
  kind: "home",
  label: "Home",
  route: "/home",
};

export function createInitialState(): WorkspaceTabsState {
  return { tabs: [HOME_TAB], activeId: HOME_TAB.id };
}

export function isHomeTab(tab: Pick<WorkTab, "id">): boolean {
  return tab.id === HOME_TAB.id;
}

/**
 * Open a work tab.
 *
 * Dedupe is by **`id`** — the stable work-object key — per PRD-0096 §9.2.3. It used to be
 * by `route`, which meant one work object opened as several tabs whenever the operator
 * drilled into it: an analytics property, its source, and a keyword under it are one
 * investigation but three routes. Those tabs shared an `id`, giving duplicate React keys
 * and making `closeTab`/`activateTab` ambiguous (both resolve by first match).
 *
 * Re-opening an already-open object ADVANCES its route rather than adding a tab, so the
 * strip keeps one entry and re-activating it restores the deepest position the operator
 * reached. Label follows too, so the strip shows where you are.
 *
 * Home (`route === HOME_TAB.route`) just re-activates the pinned Home tab.
 *
 * This also satisfies §A.16.1's "route-update operation" prerequisite without a separate
 * function: `registerTabFromUrl` already routes every in-shell navigation through here, so
 * advancing an open tab IS the update path. A standalone updateTabRoute() would have been
 * dead code.
 */
export function openTab(
  state: WorkspaceTabsState,
  tab: WorkTab,
): WorkspaceTabsState {
  if (tab.route === HOME_TAB.route) {
    return { ...state, activeId: HOME_TAB.id };
  }

  const existing = state.tabs.find((t) => t.id === tab.id);
  if (existing) {
    return {
      tabs: state.tabs.map((t) =>
        t.id === tab.id ? { ...t, route: tab.route, label: tab.label } : t,
      ),
      activeId: existing.id,
    };
  }

  return {
    tabs: [...state.tabs, tab],
    activeId: tab.id,
  };
}


/**
 * Close a work tab.
 * - Home is never closeable — closing it is a no-op.
 * - If the closed tab was active, activate the adjacent tab (prefer the one to
 *   the right, fall back to the one to the left); if it was the last work tab,
 *   land on Home.
 * - Closing a non-active tab keeps the current active tab.
 */
export function closeTab(
  state: WorkspaceTabsState,
  id: string,
): WorkspaceTabsState {
  if (id === HOME_TAB.id) {
    return state;
  }

  const index = state.tabs.findIndex((t) => t.id === id);
  if (index === -1) {
    return state;
  }

  const tabs = state.tabs.filter((t) => t.id !== id);

  if (state.activeId !== id) {
    return { ...state, activeId: state.activeId, tabs };
  }

  // The closed tab was active — pick the adjacent tab.
  // `tabs` no longer contains the closed tab, so the tab that was to its right
  // now sits at `index`; the one to its left sits at `index - 1`.
  const next = tabs[index] ?? tabs[index - 1] ?? HOME_TAB;
  return { tabs, activeId: next.id };
}

// ---------------------------------------------------------------------------
// Per-brand wrapper layer (PR3).
//
// Tabs persist PER BRAND so switching brands doesn't reset the operator's open
// work tabs. This is a thin, pure layer ON TOP of the single-brand reducer
// above (which is unchanged): a map of brandId → WorkspaceTabsState. The React
// context picks the active brand's slice and runs the same reducers on it.
// ---------------------------------------------------------------------------

/** Map of brand id → that brand's tab state. */
export type MultiBrandTabsState = Record<string, WorkspaceTabsState>;

/**
 * Stable slice key used when there is no active brand (brandId null/undefined).
 * Non-colliding sentinel so a real brand id can never alias the "no brand" slice.
 */
export const NO_BRAND_KEY = "__no_brand__";

/** Normalize a possibly-missing brandId to a stable, non-colliding slice key. */
export function brandSliceKey(brandId: string | null | undefined): string {
  return brandId ?? NO_BRAND_KEY;
}

/**
 * Read a brand's tab state from the map, lazily defaulting unknown brands to a
 * fresh Home-only state. Pure: never mutates `state`.
 */
export function getBrandState(
  state: MultiBrandTabsState,
  brandId: string | null | undefined,
): WorkspaceTabsState {
  return state[brandSliceKey(brandId)] ?? createInitialState();
}

/**
 * Return a NEW map with `brandId`'s slice replaced by `next`. Pure: other
 * brands' slices are preserved by reference (no mutation).
 */
export function withBrandState(
  state: MultiBrandTabsState,
  brandId: string | null | undefined,
  next: WorkspaceTabsState,
): MultiBrandTabsState {
  return { ...state, [brandSliceKey(brandId)]: next };
}
